import type { EnvironmentalTopEmitter } from '@siliconx/shared';
import { formatTonnes } from '../format';

/** SIL-476 — "Top emitting assets" cards, ranked (Scope 2 + Scope 3)
 * descending. No sparkline (the mock's per-asset trend line has no serving
 * surface today — a per-asset monthly history would need a whole new
 * aggregation this ticket does not add; the per-class/per-site aggregates
 * above are the fleet-detail-scoped time series this ticket ships). */
export function TopEmittingAssets({ items }: { items: EnvironmentalTopEmitter[] }) {
  if (items.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-zinc-400">No emitting assets resolved yet.</div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((a) => {
        const s2Pct = a.totalKgco2e > 0 ? (a.scope2Kgco2e / a.totalKgco2e) * 100 : 0;
        const s3Pct = a.totalKgco2e > 0 ? (a.scope3Kgco2e / a.totalKgco2e) * 100 : 0;
        return (
          <div
            key={a.assetId}
            className="flex items-center gap-4 rounded-lg border border-zinc-200 bg-white px-4 py-3"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate font-mono text-[13px] text-emerald-700">{a.assetId.slice(0, 8)}</span>
              <span className="truncate text-sm font-medium">{a.displayName}</span>
              <div className="flex h-1.5 max-w-[150px] overflow-hidden rounded-sm bg-zinc-100">
                <div className="h-full bg-indigo-500" style={{ width: `${s2Pct}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${s3Pct}%` }} />
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-0.5">
              <span className="font-mono text-sm font-semibold tabular-nums">{formatTonnes(a.totalKgco2e)}</span>
              <span className="font-mono text-[13px] text-zinc-500">
                S2 {formatTonnes(a.scope2Kgco2e)} · S3 {formatTonnes(a.scope3Kgco2e)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
